/**
 * Mapa tipo → componente que o desenha.
 *
 * Este arquivo é o ÚNICO lugar do sistema que decide quem desenha o quê. É o
 * substituto do `ngSwitch` de quatro níveis, e o ponto de troca de um widget:
 * quando um campo nativo ficar pronto, muda uma linha aqui, e o rollback é
 * reverter essa linha.
 *
 * Estado da Fase 3, dito sem maquiagem:
 *
 *  - **containers**: nativos, prontos, sobre a grade de 24 colunas;
 *  - **16 campos**: pela ponte `webhook-common` — reuso deliberado de widgets já
 *    testados em produção (seção 4.5 do plano);
 *  - **o resto** (`image`, `datagrid`, `spreadsheet`, `treeview`, `attributeSet`,
 *    `customComponent`, `dashboardCard`, `dashboardEmbed`): marcador. O `datagrid`
 *    e o `master-detail-panel` chegam na Fase 6, quando MUDAM DE PASTA e ganham
 *    OnPush — mover e sanear, não reescrever. Os do dashboard chegam com a ponte
 *    da seção 2.3.
 *
 * O marcador é escolha, não esquecimento: um mapa incompleto faria o nó sumir da
 * tela sem aviso, que é a classe de bug que a migração encontrou em produção (22
 * campos órfãos que simplesmente não renderizam). `renderer-map.spec.ts` afirma
 * que todo `NodeType` tem entrada.
 */
import { Provider, Type } from '@angular/core';

import { NODE_TYPES, NodeType } from '../model/node-props';
import { NODE_RENDERERS } from './node-renderer';
import { WebhookFieldRenderer } from './bridge/webhook-field.renderer';
import { DashboardCardRenderer } from './bridge/dashboard-card.renderer';
import { DataGridRenderer } from '../components/data-grid/data-grid.renderer';
import { DashboardEmbedRenderer } from './bridge/dashboard-embed.renderer';
import { ButtonFieldRenderer } from './fields/button.renderer';
import { TextAreaFieldRenderer } from './fields/textarea.renderer';
import { DividerFieldRenderer } from './fields/divider.renderer';
import { ImageFieldRenderer } from './fields/image.renderer';
import { TreeViewFieldRenderer } from './fields/treeview.renderer';
import { CustomComponentRenderer } from './fields/custom-component.renderer';
import { AttributeSetRenderer } from './fields/attribute-set.renderer';
import { SpreadsheetRenderer } from '../components/spreadsheet/spreadsheet.renderer';
import { PanelContainerRenderer } from './containers/panel.renderer';
import { RowContainerRenderer } from './containers/row.renderer';
import { ScreenContainerRenderer } from './containers/screen.renderer';
import { SectionContainerRenderer } from './containers/section.renderer';
import { TabContainerRenderer } from './containers/tab.renderer';
import { TabsContainerRenderer } from './containers/tabs.renderer';
import { NodeRendererMap } from './node-renderer';
import { NotImplementedRenderer } from './not-implemented.renderer';

export const BUILTIN_RENDERERS: NodeRendererMap = {
  // containers — nativos
  screen: ScreenContainerRenderer as Type<never>,
  tabs: TabsContainerRenderer as Type<never>,
  tab: TabContainerRenderer as Type<never>,
  panel: PanelContainerRenderer as Type<never>,
  row: RowContainerRenderer as Type<never>,
  section: SectionContainerRenderer as Type<never>,

  // campos — ponte com o webhook-common
  text: WebhookFieldRenderer as Type<never>,
  // Nativo: o `nzAutosize` do widget legado escreve `height` inline, e
  // estilo inline vence folha de estilo — o campo nunca acompanhava a
  // altura reservada pelo layout. Ver `textarea.renderer.ts`.
  textarea: TextAreaFieldRenderer as Type<never>,
  number: WebhookFieldRenderer as Type<never>,
  date: WebhookFieldRenderer as Type<never>,
  switch: WebhookFieldRenderer as Type<never>,
  select: WebhookFieldRenderer as Type<never>,
  dropdown: WebhookFieldRenderer as Type<never>,
  querySelect: WebhookFieldRenderer as Type<never>,
  search: WebhookFieldRenderer as Type<never>,
  tag: WebhookFieldRenderer as Type<never>,
  keyValue: WebhookFieldRenderer as Type<never>,
  keyValueObject: WebhookFieldRenderer as Type<never>,
  // Nativo: o `nz-divider` vertical desenha 1em fixo e nao acompanha a altura
  // do vizinho, e o traco e a cor eram cravados. Ver `divider.renderer.ts`.
  divider: DividerFieldRenderer as Type<never>,
  // Nativo, e nao pela ponte: o widget legado cravava `nzType="primary"`
  // e nao emitia clique. Ver `button.renderer.ts`.
  button: ButtonFieldRenderer as Type<never>,
  simpletable: WebhookFieldRenderer as Type<never>,
  table: WebhookFieldRenderer as Type<never>,

  // ainda sem renderer nativo — ver o cabeçalho
  image: ImageFieldRenderer as Type<never>,
  datagrid: DataGridRenderer as Type<never>,
  spreadsheet: SpreadsheetRenderer as Type<never>,
  treeview: TreeViewFieldRenderer as Type<never>,
  attributeSet: AttributeSetRenderer as Type<never>,
  customComponent: CustomComponentRenderer as Type<never>,
  dashboardCard: DashboardCardRenderer as Type<never>,
  dashboardEmbed: DashboardEmbedRenderer as Type<never>
};

/** Tipos que já têm renderer de verdade — o placar da Fase 3. */
export function typesWithNativeRenderer(): readonly NodeType[] {
  const done: NodeType[] = [];

  for (const type of NODE_TYPES) {
    if (BUILTIN_RENDERERS[type] !== (NotImplementedRenderer as unknown)) {
      done.push(type);
    }
  }

  return done;
}

/** Tipos que ainda caem no marcador. */
export function typesPendingRenderer(): readonly NodeType[] {
  const pending: NodeType[] = [];

  for (const type of NODE_TYPES) {
    if (BUILTIN_RENDERERS[type] === (NotImplementedRenderer as unknown)) {
      pending.push(type);
    }
  }

  return pending;
}

/**
 * O mapa nativo como provider, para quem hospeda uma tela.
 *
 * Funcao em vez de constante pelo mesmo motivo de sempre neste projeto: um
 * objeto montado no corpo do modulo e avaliado na CARGA, e este arquivo
 * participa de um ciclo de imports — o mapa puxa os renderers, que puxam o
 * `NodeHost`, que puxa o mapa. `useFactory` roda quando o injetor cria o valor.
 */
export function nativeRenderers(): NodeRendererMap {
  return { ...BUILTIN_RENDERERS };
}

export const NATIVE_RENDERERS_PROVIDER: Provider = {
  provide: NODE_RENDERERS,
  useFactory: nativeRenderers
};
